import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { Suspense, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { InternShopifyTokenDialogQuery } from "./__generated__/InternShopifyTokenDialogQuery.graphql";
import useCycleShopifyTokenMutation from "../../../mutations/useCycleShopifyTokenMutation";
import useRefreshShopifyTokenMutation from "../../../mutations/useRefreshShopifyTokenMutation";
import useTestShopifyTokenMutation from "../../../mutations/useTestShopifyTokenMutation";

const tokenQuery = graphql`
  query InternShopifyTokenDialogQuery($brandId: ID!) {
    internShopifyTokenStatus(brandId: $brandId) {
      state
      accessTokenLast4
      accessTokenExpiresAt
      refreshTokenExpiresAt
      refreshedAt
      error
      adminChangeUrl
    }
  }
`;

type Props = {
  brandId: string;
  displayName: string;
  isSuperuser: boolean;
  onClose: () => void;
};

const formatWhen = (value: string | null | undefined) => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

const TokenStatus = ({
  brandId,
  displayName,
  isSuperuser,
  fetchKey,
  onClose,
  onChanged,
}: Props & { fetchKey: number; onChanged: () => void }) => {
  const data = useLazyLoadQuery<InternShopifyTokenDialogQuery>(
    tokenQuery,
    { brandId },
    { fetchKey, fetchPolicy: "network-only" }
  );
  const status = data.internShopifyTokenStatus;
  const [confirmCycle, setConfirmCycle] = useState(false);
  const [cycleToken, isCycleInFlight] = useCycleShopifyTokenMutation();
  const [testToken, isTestInFlight] = useTestShopifyTokenMutation();
  const [refreshToken, isRefreshInFlight] = useRefreshShopifyTokenMutation();
  const isBusy = isCycleInFlight || isTestInFlight || isRefreshInFlight;
  const canCycle = status.state === "non-expiring (legacy)";
  const adminUrl =
    isSuperuser && status.adminChangeUrl ? status.adminChangeUrl : null;

  return (
    <>
      <DialogTitle>Shopify token</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Typography variant="body2">{displayName}</Typography>
          <Typography variant="body2">State: {status.state}</Typography>
          <Typography variant="body2">
            Access token:{" "}
            {status.accessTokenLast4 ? `••••${status.accessTokenLast4}` : "—"}
          </Typography>
          {status.state === "expiring" && (
            <>
              <Typography variant="body2">
                Last refreshed: {formatWhen(status.refreshedAt)}
              </Typography>
              <Typography variant="body2">
                Access token expires: {formatWhen(status.accessTokenExpiresAt)}
              </Typography>
              <Typography variant="body2">
                Refresh token expires:{" "}
                {formatWhen(status.refreshTokenExpiresAt)}
              </Typography>
            </>
          )}
          {status.state === "disconnected" && status.error && (
            <Alert severity="error">{status.error}</Alert>
          )}
          {status.state !== "disconnected" && status.error && (
            <Alert severity="warning">{status.error}</Alert>
          )}
          {adminUrl && (
            <Button
              href={adminUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              startIcon={<OpenInNewIcon />}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Django admin
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          Close
        </Button>
        <Button onClick={() => testToken(brandId, onChanged)} disabled={isBusy}>
          Test
        </Button>
        <Button
          onClick={() => refreshToken(brandId, onChanged)}
          disabled={isBusy || status.state !== "expiring"}
        >
          Refresh
        </Button>
        <Button
          onClick={() => setConfirmCycle(true)}
          disabled={isBusy || !canCycle}
          color="warning"
        >
          Switch to expiring token
        </Button>
      </DialogActions>
      <Dialog open={confirmCycle} onClose={() => setConfirmCycle(false)}>
        <DialogTitle>Cycle to an expiring token?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This destroys the permanent token immediately. Do this only from the
            environment that owns the install, and not while the daily order
            sync is using the token.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCycle(false)} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            disabled={isBusy}
            onClick={() => {
              setConfirmCycle(false);
              cycleToken(brandId, onChanged);
            }}
          >
            Cycle token
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const InternShopifyTokenDialog = (props: Props) => {
  const [fetchKey, setFetchKey] = useState(0);

  return (
    <Dialog open onClose={props.onClose} fullWidth>
      <Suspense
        fallback={
          <>
            <DialogTitle>Shopify token</DialogTitle>
            <DialogContent>
              <Typography variant="body2">Loading token status…</Typography>
            </DialogContent>
          </>
        }
      >
        <TokenStatus
          {...props}
          fetchKey={fetchKey}
          onChanged={() => setFetchKey((key) => key + 1)}
        />
      </Suspense>
    </Dialog>
  );
};

export default InternShopifyTokenDialog;
